import { Box } from '@chakra-ui/react';
import {
  LuFileText,
  LuFileSpreadsheet,
  LuImage,
  LuFile,
} from 'react-icons/lu';
import { useTranslation } from 'react-i18next';

// A colour's tint, one per mode, for the ground under a mark or a word in
// that colour. Spread it: `{...TINT.red}` sets `bg` and its `_dark`.
export const TINT = {
  red:    { bg: 'rgba(185, 28, 28, 0.08)', _dark: { bg: 'rgba(248, 113, 113, 0.14)' } },
  green:  { bg: 'rgba(21, 128, 61, 0.10)', _dark: { bg: 'rgba(74, 222, 128, 0.14)' } },
  blue:   { bg: 'redesign.bg.accent' },
  orange: { bg: 'rgba(234, 106, 59, 0.12)', _dark: { bg: 'rgba(239, 117, 71, 0.14)' } },
};

// Red is kept for a file that failed, so a PDF, the usual statement, is the
// warm orange; images are rare and their icon tells them apart.
export const FILE_TYPE_META = {
  pdf:   { icon: LuFileText,        color: 'redesign.accent.expense', ...TINT.orange },
  csv:   { icon: LuFileSpreadsheet, color: 'redesign.accent.green',   ...TINT.green },
  xls:   { icon: LuFileSpreadsheet, color: 'redesign.accent.blue',    ...TINT.blue },
  xlsx:  { icon: LuFileSpreadsheet, color: 'redesign.accent.blue',    ...TINT.blue },
  excel: { icon: LuFileSpreadsheet, color: 'redesign.accent.blue',    ...TINT.blue },
  jpg:   { icon: LuImage,           color: 'redesign.text.muted',     bg: 'redesign.bg.card' },
  jpeg:  { icon: LuImage,           color: 'redesign.text.muted',     bg: 'redesign.bg.card' },
  png:   { icon: LuImage,           color: 'redesign.text.muted',     bg: 'redesign.bg.card' },
};

export const fileMeta = (fileType) =>
  FILE_TYPE_META[fileType?.toLowerCase()] || {
    icon: LuFile,
    color: 'redesign.text.muted',
    bg: 'redesign.bg.card',
  };

export const STATUS_CONFIG = {
  completed: { labelKey: 'processed',  color: 'redesign.amount.income', ...TINT.green },
  running:   { labelKey: 'processing', color: 'redesign.accent.blue',   ...TINT.blue },
  // Uploaded and waiting its turn: a workspace's statements run one at a time.
  pending:   { labelKey: 'waiting',    color: 'redesign.accent.blue',   ...TINT.blue },
  failed:    { labelKey: 'failed',     color: 'redesign.accent.red',    ...TINT.red },
  cancelled: { labelKey: 'cancelled',  color: 'redesign.text.muted',   bg: 'redesign.bg.card' },
};

export function StatusPill({ status }) {
  const { t } = useTranslation('sourceFiles');
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.cancelled;
  return (
    <Box
      px={2}
      py="2px"
      borderRadius="full"
      bg={cfg.bg}
      _dark={cfg._dark}
      fontSize="2xs"
      fontWeight={600}
      color={cfg.color}
      flexShrink={0}
    >
      {t(`status.${cfg.labelKey}`)}
    </Box>
  );
}

export const isActiveStatus = (status) => status === 'running' || status === 'pending';

export const MAX_UPLOAD_FILES = 15;
